import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Avatar,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { CloudUpload, Delete, PhotoCamera } from '@mui/icons-material';
import { filesAPI } from '../services/api';

interface ProfilePictureUploadProps {
  currentImageUrl?: string | null;
  entityType: 'USER' | 'EMPLOYEE';
  entityId: string;
  onImageChange: (imageUrl: string | null, file?: File) => void;
  disabled?: boolean;
  tempImageUrl?: string; // For temporary storage before entity creation
}

const ProfilePictureUpload: React.FC<ProfilePictureUploadProps> = ({
  currentImageUrl,
  entityType,
  entityId,
  onImageChange,
  disabled = false,
  tempImageUrl,
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    setUploading(true);
    setError('');

    try {
      // If entityId is "new", create a temporary URL for preview
      if (entityId === 'new') {
        const tempUrl = URL.createObjectURL(file);
        onImageChange(tempUrl, file);
      } else {
        const response = await filesAPI.uploadProfilePicture(file, entityType, entityId);
        onImageChange(response.profilePictureUrl);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploading(false);
      // Reset the input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = () => {
    onImageChange(null);
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
      <Box sx={{ position: 'relative' }}>
        <Avatar
          src={currentImageUrl || tempImageUrl || undefined}
          sx={{
            width: 120,
            height: 120,
            fontSize: '3rem',
            bgcolor: 'primary.main',
          }}
        >
          {!currentImageUrl && !tempImageUrl && <PhotoCamera />}
        </Avatar>
        
        {uploading && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              borderRadius: '50%',
            }}
          >
            <CircularProgress size={40} color="inherit" />
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Button
          variant="outlined"
          startIcon={<CloudUpload />}
          onClick={handleButtonClick}
          disabled={uploading || disabled}
          size="small"
        >
          {(currentImageUrl || tempImageUrl) ? 'Change Photo' : 'Upload Photo'}
        </Button>
        
        {(currentImageUrl || tempImageUrl) && (
          <Tooltip title="Remove profile picture">
            <IconButton
              color="error"
              onClick={handleRemoveImage}
              disabled={uploading || disabled}
              size="small"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {error && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: 300 }}>
          {error}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary" align="center">
        Supported formats: JPG, PNG, GIF. Max size: 5MB
      </Typography>
    </Box>
  );
};

export default ProfilePictureUpload;
